import React from "react";
import { FiFileText, FiCheckCircle, FiClock } from "react-icons/fi";

const DeclarationListItem = ({
  declaration,
  isActive,
  onClick,
}) => {
  // Determine status badge styling
  const getStatusStyle = () => {
    if (declaration.e_sign) {
      return {
        bg: 'bg-emerald-50 border-emerald-200',
        text: 'text-emerald-600',
        icon: '✅',
        label: 'Signed'
      };
    }
    return {
      bg: 'bg-amber-50 border-amber-200',
      text: 'text-amber-600',
      icon: '⏱️',
      label: 'Pending'
    };
  };

  const statusStyle = getStatusStyle();

  // Format date
  const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  // Get reason display text
  const getReasonDisplay = (reason) => {
    switch (reason) {
      case 'Term_And_Condition':
        return 'Terms & Conditions';
      case 'Expected_Delivery_Time':
        return 'Delivery Agreement';
      case 'Single_Quotation':
        return 'Quotation Declaration';
      case 'Cart_Of_Quotations':
        return 'Multi-Quotation Declaration';
      default:
        return reason || 'Declaration';
    }
  };

  const getDeclarationType = (type) => {
    switch (type) {
      case 'Term_And_Condition':
        return '📋';
      case 'Expected_Delivery_Time':
        return '📅';
      case 'Single_Quotation':
        return '💰';
      case 'Cart_Of_Quotations':
        return '🛒';
      default:
        return '📄';
    }
  };

  return (
    <div
      onClick={onClick}
      className={`group relative rounded-xl border cursor-pointer transition-all duration-200 overflow-hidden
        ${isActive
          ? 'border-[#772037] bg-[#772037]/[0.03] shadow-md shadow-[#772037]/10'
          : 'border-gray-200 bg-white hover:border-[#772037]/40 hover:shadow-sm'
        }`}
    >
      {/* Active accent bar */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl transition-all duration-200
        ${isActive
            ? 'bg-gradient-to-b from-[#772037] to-[#CC234F]'
            : 'bg-transparent group-hover:bg-[#772037]/20'
          }`}
      />

      <div className="pl-3 pr-3 pt-3 pb-3 ml-1">
        {/* Top row: icon + reason + status */}
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div
            className={`w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 border flex items-center justify-center text-2xl
            ${isActive ? 'border-[#772037]/20 bg-[#772037]/5' : 'border-gray-100 bg-gray-50'}`}
          >
            {getDeclarationType(declaration.declaration_is_for)}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p
              className={`text-[10px] font-bold uppercase tracking-widest mb-0.5
              ${isActive ? 'text-[#772037]/70' : 'text-gray-400'}`}
            >
              {declaration.declaration_is_for?.replace(/_/g, ' ') || 'Declaration'}
            </p>
            <h3
              className={`font-bold text-sm leading-tight truncate
              ${isActive ? 'text-[#772037]' : 'text-gray-800'}`}
            >
              {getReasonDisplay(declaration.reason) || 'Declaration'}
            </h3>

            {/* Sub info */}
            {declaration.orderId || declaration.quotationId ? (
              <p className="text-xs text-gray-400 font-medium truncate mt-0.5">
                {declaration.orderId ? `Order: #${declaration.orderId.toString().slice(-6)}` : ''}
                {declaration.quotationId ? `Quotation: #${declaration.quotationId.toString().slice(-6)}` : ''}
              </p>
            ) : null}

            {/* Status badge */}
            <div className="flex items-center gap-1.5 mt-1.5">
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusStyle.bg} ${statusStyle.text}`}
              >
                <span>{statusStyle.icon}</span>
                {statusStyle.label}
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="my-2.5 border-t border-dashed border-gray-100" />

        {/* Bottom row: Date */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 text-gray-400">
            <svg
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="text-[10px] font-medium">
              {fmtDate(declaration.createdAt || declaration.date)}
            </span>
          </div>

          {/* E-signature indicator */}
          {declaration.e_sign && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[9px] font-bold text-emerald-600">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
              </svg>
              Signed
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeclarationListItem;